import React, { createContext, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/react-hooks';
import { useMutation } from '@apollo/client';
import { useGlobalContext } from '../../../../../apps/main/ui/GlobalContext';
import useGetDicti from '../../../common/hooks/useGetDicti';
import {
	ChangeLogTableEnum,
	DictKeyEnum,
	GroupsQuery,
	GroupsQueryVariables,
	Restriction,
	RestrictionsMutation,
	RestrictionsMutationVariables,
	UsersQuery,
	UsersQueryVariables,
} from '../../../../../libs/api/graphqlTypes';
import { GROUPS, USERS } from '../../../../../libs/api/queries';
import { ISelectItem } from '../../../../../libs/uiKit/select/types';
import { IForm } from '../../../../../libs/uiKit/fieldsBuilder/types';
import { IEnumItem } from '../../../../../libs/uiKit/enums/types';
import useNotification from '../../../../widgets/notifier/ui/hooks/useNitification';
import { UPDATE_RESTRICTION } from '../../../../../libs/api/commands';
import {Text3} from "../../../../../libs/uiKit/globalStyledComponents";
import {TimeReloadIcon} from "../../../../../icons/ui/TimeReload";
import Button from "../../../../../libs/uiKit/button";
import {useUserRights} from "../../../common/hooks/useUserRights";
import {useNavigate} from "react-router-dom";
import Table from "../../../../../libs/uiKit/table";
import CheckboxField from "../../../../../libs/uiKit/fieldsBuilder/fields/CheckboxField";
import InputField from "../../../../../libs/uiKit/fieldsBuilder/fields/InputField";
import SelectField from "../../../../../libs/uiKit/fieldsBuilder/fields/SelectField";
import {StyledTabularForm} from "../styles";
import NumberField from "../../../../../libs/uiKit/fieldsBuilder/fields/NumberField";
import {
    DECLARATION_DISABLED_FOR,
    DECLARATION_PROPERTY_MATERIAL_SHOW_FOR,
    PROPERTY_HIDE_WOOD_ELEMENTS_FOR,
    PROPERTY_HIDE_YEAR_FOR, PROPERTY_MATERIAL_SHOW_FOR
} from "../../../../../libs/utils/staticData";

type SettingsContext = {
	form: IForm;
	onFormSubmit: () => void;
	isLoading: boolean;
	stateIsOpenHistory: boolean;
	setIsOpenHistory: (value: boolean) => void;
    stateCopyConfirmModal: boolean;
    setCopyConfirmModal: (value: boolean) => void;
	onHistoryClick: () => void;
    onCopyFromConfirmed: () => void;
	historyData: any;
	isDisabledEdit: boolean;
};

type SettingForm = {
	propTable: any[];
	autoscoring?: boolean;
	autoscoringLife?: boolean;
	autoscoringProperty?: boolean;
	autoscoringTitle?: boolean;
	declaration?: boolean;
	declarationLife?: boolean;
	declarationProperty?: boolean;
	declarationTitle?: boolean;
	ageLimit?: string;
	userOrGroup: ISelectItem;
    copyFrom: ISelectItem|null;
	banks?: IEnumItem[];
	declarationAgeLimit?: string;
	declarationFloorMaterials?: IEnumItem[];
	declarationObjectType?: IEnumItem[];
	declarationSummLife?: number;
	declarationSummProperty?: number;
	declarationWallMaterials?: IEnumItem[];
	declarationYearOfBuild?: string;
	maxDiscount?: number;
	contragentMaxAge?: number;
	objectType?: IEnumItem[];
	summLife?: number;
	summProperty?: number;
	yearOfBuild?: string;
	comment: string;
	declTable: any;
};

export type Restrictions = {
	autoscoring?: boolean;
	autoscoringLife?: boolean;
	autoscoringProperty?: boolean;
	autoscoringTitle?: boolean;
	declaration?: boolean;
	declarationLife?: boolean;
	declarationProperty?: boolean;
	declarationTitle?: boolean;
	ageLimit?: string;
	banks?: string[];
	declarationAgeLimit?: string;
	declarationSummLife?: number;
	declarationSummProperty?: number;
	declarationYearOfBuild?: string;
	maxDiscount?: number;
	contragentMaxAge?: number;
	summLife?: number;
	summProperty?: number;
	yearOfBuild?: string;
	propTable?: any[];
};

type UserGroupItem = { restrictions: Restriction[]; type: 'group' | 'user' } & ISelectItem;

const SettingsContext = createContext<SettingsContext>({} as SettingsContext);

export const useSettingsContext = (): SettingsContext => useContext(SettingsContext);

const SettingsContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const {
		user: { isSuper, restrictions, isAdmin, isStaffMember, id: userId },
		routes: { error500 },
	} = useGlobalContext();

	const { rightsHandler } = useUserRights();
	const navigate = useNavigate();

	if(userId && !(isSuper || isStaffMember)){
		navigate('/');
	}

	const { setNotification } = useNotification();
	const formHook = useForm<SettingForm>({ mode: 'onBlur' });

	const isDisabledEdit = !isSuper;

	const { data: selectPledgeTypes, loading: loadingPledgeTypes } = useGetDicti(DictKeyEnum.PropertyType);
	const { data: selectWallMaterial, loading: loadingWallMaterial } = useGetDicti(DictKeyEnum.WallMaterial);
	const { data: selectFloorMaterial, loading: loadingFloorMaterial } = useGetDicti(DictKeyEnum.FloorMaterial);
	const { data: selectBanks, loading: loadingBanks } = useGetDicti(DictKeyEnum.CreditBank);

	const [stateIsOpenHistory, setIsOpenHistory] = React.useState<boolean>(false);
	const [stateHistoryData, setStateHistoryData] = React.useState<any>(null);
    const [stateCopyConfirmModal, setCopyConfirmModal] = React.useState<boolean>(false);

	const { data: dataGroups, loading: loadingGroups } = useQuery<GroupsQuery, GroupsQueryVariables>(GROUPS, { onError: error => error500(error) });
	const { data: dataUsers, loading: loadingUsers } = useQuery<UsersQuery, UsersQueryVariables>(USERS, { onError: error => error500(error) });
	const [updateRestriction, { loading: loadingUpdateRestriction }] = useMutation<RestrictionsMutation, RestrictionsMutationVariables>(UPDATE_RESTRICTION, {
		refetchQueries: ['users', 'groups'],
	});

	const selectGroups: UserGroupItem[] = (dataGroups?.groups || []).map(group => ({
		label: group?.name || '',
		type: 'group',
		value: `group-${group?.id}`,
		restrictions: (group?.restrictions || []).map(restriction => ({ tag: restriction?.tag, value: restriction?.value })),
	}));
	const selectUsers: UserGroupItem[] = (dataUsers?.users || []).map(user => ({
		label: user?.full_name && user?.username ? `${user?.full_name} (${user?.username})` : 'unknown',
		type: 'user',
		value: `user-${user?.id}`,
		restrictions: (user?.restrictions || []).map(restriction => ({ tag: restriction?.tag, value: restriction?.value })),
	}));
	const groupsUsers: UserGroupItem[] = React.useMemo(() => [...selectUsers, ...selectGroups], [selectGroups, selectUsers]);

	const [isAutoscoring, isAutoscoringLife, isAutoscoringPropery, isDeclaration, isDeclarationLife, isDeclarationProperty, userOrGroup, copyFrom] = formHook.watch([
		'autoscoring',
		'autoscoringLife',
		'autoscoringProperty',
		'declaration',
		'declarationLife',
		'declarationProperty',
		'userOrGroup',
        'copyFrom'
	]);

	const {
		autoscoring,
		autoscoringLife,
		autoscoringProperty,
		autoscoringTitle,
		declaration,
		declarationLife,
		declarationProperty,
		declarationTitle,
		ageLimit,
		banks,
		declarationAgeLimit,
		declarationSummLife,
		declarationSummProperty,
		declarationYearOfBuild,
		maxDiscount,
		contragentMaxAge,
		summLife,
		summProperty,
		yearOfBuild,
	} = (isSuper ? {} : restrictions) as Restrictions;

	const setupDefaultValueHandler = React.useCallback(
		(userOrGroup: ISelectItem | null) => {
			if (!userOrGroup) return;

			const curValues = formHook.getValues();
			Object.entries(curValues).forEach(([key, value]) => {
				if (key === 'userOrGroup') return;
				if(key === 'declTable' || key === 'propTable'){
					Object.entries(value).forEach(([key1, value1]) => {
						if(typeof value1 === 'object'){
							// @ts-ignore
							Object.entries(value1).forEach(([key2, value2]) => {
								// @ts-ignore
								formHook.setValue(`${key}[${key1}][${key2}]`, undefined);
								if(key2 === 'wall'){
									selectWallMaterial.forEach((material => {
										// @ts-ignore
										formHook.setValue(`${key}[${key1}][${key2}][${material.value}]`, undefined);
									}));
								}
								if(key2 === 'floor'){
									selectFloorMaterial.forEach((material => {
										// @ts-ignore
										formHook.setValue(`${key}[${key1}][${key2}][${material.value}]`, undefined);
									}));
								}
							})
						}
					})
				}
				formHook.setValue(key as any, (key === 'banks') ? [] : undefined);
			});

			const findRestriction: Restriction[] | undefined = groupsUsers.find(({ value }) => value === userOrGroup.value)?.restrictions;
			if (!findRestriction || !findRestriction.length) return;
			const restrictionsToObject: Restrictions = findRestriction.reduce<Restrictions>((prev, { tag, value }) => ({ ...prev, [tag || 'unknown']: value }), {});
			const restrictionsToSet = {
				...restrictionsToObject,
				banks: (restrictionsToObject.banks || []).map((bank: string) => selectBanks.find(({ value }) => value === bank)),
			};
			Object.entries(restrictionsToSet).map(([tag, value]) => {
				if(tag === 'propTable' || tag === 'declTable'){
					let value2 = {};
					Object.entries(value).forEach(([pledgeKey, pledge]) => {
						const wall = Object.entries(pledge?.wall || {}).reduce((acc: any, [key, value]) => {
							if(value) acc[String(value)] = true;
							return acc;
						}, {});
						const floor = Object.entries(pledge?.floor || {}).reduce((acc: any, [key, value]) => {
							if(value) acc[String(value)] = true;
							return acc;
						}, {});
						// @ts-ignore
						value2[pledgeKey] = {
							...pledge,
							wall,
							floor,
						}
					})
					return formHook.setValue(tag as any, value2);
				}
				return formHook.setValue(tag as any, value);
			});
		},
		[formHook, groupsUsers, selectBanks, selectPledgeTypes, selectWallMaterial]
	);

	const onHistoryClick = () => {
		const findUserOrGroup = groupsUsers.find(({ value }) => value === userOrGroup.value);
		if (!findUserOrGroup) return setNotification({ type: 'error', text: 'no user or group' });
		const id: string = findUserOrGroup.value.split('-')[1];
		setStateHistoryData({
			id: parseInt(id, 10),
			model: findUserOrGroup.type === 'user' ? ChangeLogTableEnum.RestrictionsForUser : ChangeLogTableEnum.RestrictionsForGroup,
		});
		setIsOpenHistory(true);
	}

    const onCopyFromClick = () => {
        setCopyConfirmModal(true);
    }
    const onCopyFromConfirmed = () => {
        setupDefaultValueHandler(copyFrom);
        formHook.setValue('copyFrom', null);
    }

	const onFormSubmit = formHook.handleSubmit(formData => {
		const data = JSON.parse(JSON.stringify(formData));
		const { userOrGroup, comment, ...rest } = data;
		const findUserOrGroup = groupsUsers.find(({ value }) => value === userOrGroup.value);
		if (!findUserOrGroup) return setNotification({ type: 'error', text: 'no user or group' });
		const id: string = findUserOrGroup.value.split('-')[1];
		if (!id) return setNotification({ type: 'error', text: 'no user or group id' });

		Object.entries(data.propTable || {}).forEach(([pledgeKey, pledge]: [string, any]) => {
			pledge.wall = Object.entries(pledge?.wall || {}).filter(([key, value]) => value).map(([key]) => key);
			pledge.floor = Object.entries(pledge?.floor || {}).filter(([key, value]) => value).map(([key]) => key);
		});
		Object.entries(data.declTable || {}).forEach(([pledgeKey, pledge]: [string, any]) => {
			pledge.wall = Object.entries(pledge?.wall || {}).filter(([key, value]) => value).map(([key]) => key);
			pledge.floor = Object.entries(pledge?.floor || {}).filter(([key, value]) => value).map(([key]) => key);
		});

		const restrictionsToSave: Restrictions = {
			...rest,
			banks: (data.banks || []).filter((bank: any) => !!bank).map((bank: { value: any; }) => String(bank.value)),
		};

		updateRestriction({
			variables: {
				groupId: findUserOrGroup.type === 'group' ? id : null,
				userId: findUserOrGroup.type === 'user' ? id : null,
				restrictions: Object.entries(restrictionsToSave).map(([tag, value]) => ({ tag, value })),
				comment,
			},
		})
			.then(() => setNotification({ type: 'success', text: 'Успешно обновлено' }))
			.catch(error => setNotification({ type: 'error', text: error }));
	});

    const setDefaultMaterials = (pledgeKey: string)=> {
        if(pledgeKey === 'p654401'){
            // @ts-ignore
            formHook.setValue(`declTable[${pledgeKey}][wall][2131871]`, true);
            // @ts-ignore
            formHook.setValue(`declTable[${pledgeKey}][floor][2084971]`, true);
        }
    }

	const getPropertySettingTable = (name: string, isDeclaration: boolean) =>
		<StyledTabularForm><Table
			total={10}
			rowsPerPage={10}
			selectedPage={1}
			head={{
				cells: [
					{
						key: 'с1',
						content: 'Объекты залога',
					},
					{
						key: 'с2',
						content: 'MIN страховая сумма',
					},
					{
						key: 'с3',
						content: 'MAX страховая сумма',
					},
					{
						key: 'с4',
						content: <>Год постройки<br/>(включительно)</>,
					},
					{
						key: 'с5',
						content: 'Материал стен',
					},
					{
						key: 'с6',
						content: 'Материал перекрытий',
					},
					{
						key: 'с7',
						content: <>Наличие дер.<br/>элементов</>,
					},
				],
			}}
			rows={selectPledgeTypes.map(pledge => {
				const pledgeKey = `p${pledge.value}`;
				// @ts-ignore
				const isRowDisabled = !formHook.watch(`${name}[${pledgeKey}][enabled]`) || isDisabledEdit;
				const isRowRequired = !isRowDisabled;
				return {
					key: `rowd${pledgeKey}`,
					cells: [
						{
							key: 'c11',
							content: <CheckboxField field={{
								fieldType: 'checkbox',
								fieldName: `${name}[${pledgeKey}][enabled]`,
								label: pledge.label,
                                isDisabled: isDeclaration ? DECLARATION_DISABLED_FOR.includes(pledge.value) : false,
                                onClick: isDeclaration ? ()=> setDefaultMaterials(pledgeKey) : undefined,
							}} formHook={formHook}/>,
						},
						{
							key: 'c12',
							content: <NumberField field={{
								fieldType: 'number',
								digitsAfterDot: 2,
								fieldName: `${name}[${pledgeKey}][sumMin]`,
								isDisabled: isRowDisabled,
								isRequired: isRowRequired,
							}} formHook={formHook}/>
						},

						{
							key: 'c13',
							content: <NumberField field={{
								fieldType: 'number',
								digitsAfterDot: 2,
								fieldName: `${name}[${pledgeKey}][sumMax]`,
								isDisabled: isRowDisabled,
								isRequired: isRowRequired,
							}} formHook={formHook}/>,
						},
						{
							key: 'c14',
							content: PROPERTY_HIDE_YEAR_FOR.includes(pledge.value) ? '' : <NumberField field={{
								fieldType: 'number',
								fieldName: `${name}[${pledgeKey}][year]`,
								isDisabled: isRowDisabled,
								isRequired: isRowRequired,
							}} formHook={formHook}/>,
						},
						{
							key: 'c15',
							content: !(isDeclaration ? DECLARATION_PROPERTY_MATERIAL_SHOW_FOR : PROPERTY_MATERIAL_SHOW_FOR).includes(pledge.value) ? '' : <>
								{selectWallMaterial.map((material, index) => {
									const materialKey = `wall${index}`;
									return <span style={{paddingRight: '10px'}}><CheckboxField key={materialKey} field={{
										fieldType: 'checkbox',
										fieldName: `${name}[${pledgeKey}][wall][${material.value}]`,
										label: material.label,
										isDisabled: isRowDisabled,
									}} formHook={formHook}/></span>;
								})}
							</>
						},
						{
							key: 'c16',
							content: !(isDeclaration ? DECLARATION_PROPERTY_MATERIAL_SHOW_FOR : PROPERTY_MATERIAL_SHOW_FOR).includes(pledge.value) ? '' : <>
								{selectFloorMaterial.map((material, index) => {
									const materialKey = `floor${index}`;
									return <span style={{paddingRight: '10px'}}><CheckboxField key={materialKey} field={{
										fieldType: 'checkbox',
										fieldName: `${name}[${pledgeKey}][floor][${material.value}]`,
										label: material.label,
										isDisabled: isRowDisabled,
									}} formHook={formHook}/></span>;
								})}
							</>,
						},
						{
							key: 'c17',
							content: !(isDeclaration ? DECLARATION_PROPERTY_MATERIAL_SHOW_FOR : PROPERTY_MATERIAL_SHOW_FOR).includes(pledge.value) ? '' : <CheckboxField field={{
								fieldType: 'checkbox',
								fieldName: `${name}[${pledgeKey}][wood]`,
								label: '',
								isDisabled: isRowDisabled,
							}} formHook={formHook}/>,
						},
					]
				}
			})}
		/></StyledTabularForm>


	const propertyAutoscoringTable = getPropertySettingTable('propTable', false);

	const propertyDeclTable = getPropertySettingTable('declTable', true);

	const form: IForm = React.useMemo(
		() => ({
			formHook,
			hotReload: true,
			fields: [
				{
					field: {
						fieldType: 'select',
						fieldName: 'userOrGroup',
						placeholder: 'Агент / группа',
						isRequired: true,
						items: groupsUsers,
						onChange: setupDefaultValueHandler,
						isLoading: loadingUsers || loadingGroups,
						errorMessage: 'Выберите агента',
						isSearchAvailable: true,
						isClearable: true,
						grid:11,
					},
				},
				{
					field: {
						fieldType: 'element',
						element: <Button tooltip="История изменений" appearance="icon" icon={<TimeReloadIcon/>}
										 onClick={onHistoryClick}
										 style={{
											 display: "inline",
											 marginLeft: "-2rem",
										 }}
						/>,
						fieldName: 'historyClick',
						grid:1,
					}
				},
                {
                    field: {
                        fieldType: 'select',
                        fieldName: 'copyFrom',
                        placeholder: 'Загрузить данные от пользователя',
                        items: selectUsers,
                        onChange: setupDefaultValueHandler,
                        isLoading: loadingUsers,
                        errorMessage: 'Выберите агента',
                        isSearchAvailable: true,
                        isClearable: true,
                        grid: (copyFrom && userOrGroup) ? 10 : 12,
                    },
                },
                {
                    isHidden: !copyFrom || !userOrGroup,
                    field: {
                        fieldType: 'element',
                        element: <Button tooltip="Подставить в поля данные этого пользователя" appearance="transparent"
                                         onClick={onCopyFromClick}
                                         style={{
                                             display: "inline",
                                             padding: "0 0.5rem",
                                         }}>Загрузить</Button>,
                        fieldName: 'copyFromClick',
                        grid:2,
                    }
                },
				{
					label: 'Исключить банки',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'enums',
						fieldName: 'banks',
						placeholder: 'Поиск по банкам',
						isLoading: loadingBanks,
						items: selectBanks,
					},
				},
				{
					label: 'Максимально возможная скидка в %',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'number',
						digitsAfterDot: 2,
						placeholder: 'Максимально возможная скидка в %',
						fieldName: 'maxDiscount',
						validationType: 'minMax',
						maxValue: maxDiscount || 100,
					},
				},
				{
					label: 'Максимально допустимый возраст заемщика/созаемщика',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'range',
						fieldName: 'contragentMaxAge',
						errorMessage: 'Выберите возрастной лимит',
						placeholder: 'Возраст',
						min: 0,
						max: 100,
						step: 1,
						validationType: Number.isNaN(contragentMaxAge) ? undefined : 'minMax',
						maxValue: contragentMaxAge,
					},
				},
				{
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'checkbox',
						label: 'Автосогласование',
						fieldName: 'autoscoring',
					},
				},
				{
					isHidden: !isAutoscoring,
					field: {
						fieldType: 'checkbox',
						label: 'Автосогласование жизни',
						fieldName: 'autoscoringLife',
						isDisabled: isDisabledEdit || !isSuper && autoscoring && !autoscoringLife,
					},
				},
				{
					isHidden: !isAutoscoring || !isAutoscoringLife,
					label: 'Максимальная страховая сумма по риску жизнь',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'number',
						digitsAfterDot: 2,
						isRequired: true,
						errorMessage: 'Введите максимальную страховую сумму по риску жизнь',
						placeholder: 'Страховая сумма по риску жизнь',
						fieldName: 'summLife',
						validationType: Number.isNaN(summLife) ? undefined : 'minMax',
						// maxValue: summLife,
					},
				},
				{
					isHidden: !isAutoscoring || !isAutoscoringLife,
					label: 'Возрастной лимит',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'range',
						isRequired: true,
						errorMessage: 'Выберите возрастной лимит',
						placeholder: 'Возраст',
						fieldName: 'ageLimit',
						min: 0,
						max: 100,
						step: 1,
						validationType: Number.isNaN(ageLimit) ? undefined : 'minMax',
						maxValue: Number(ageLimit),
					},
				},
				{
					isHidden: !isAutoscoring,
					field: {
						fieldType: 'checkbox',
						label: 'Автосогласование имущества',
						fieldName: 'autoscoringProperty',
						isDisabled: isDisabledEdit || !isSuper && autoscoring && !autoscoringProperty,
					},
				},
				{
					isHidden: !isAutoscoring || !isAutoscoringPropery,
					field: {
						fieldType: 'element',
						fieldName: 'propTable',
						element: propertyAutoscoringTable
					}
				},
				/*{
					isHidden: !isAutoscoring || !isAutoscoringPropery,
					label: 'Максимальная страховая сумма по риску имущество',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'number',
						digitsAfterDot: 2,
						isRequired: true,
						errorMessage: 'Введите максимальную страховую сумму по риску имущество',
						placeholder: 'Страховая сумма по риску имущество',
						fieldName: 'summProperty',
						validationType: Number.isNaN(summProperty) ? undefined : 'minMax',
						maxValue: summProperty,
					},
				},
				{
					isHidden: !isAutoscoring || !isAutoscoringPropery,
					label: 'Разрешенные объекты залога',
					field: {
						isDisabled: isDisabledEdit,
						isRequired: true,
						errorMessage: 'Выберите объекты залога',
						fieldType: 'enums',
						fieldName: 'objectType',
						placeholder: 'Поиск по объектам залога',
						isLoading: loadingPledgeTypes,
						items: isSuper
							? selectPledgeTypes
							: selectPledgeTypes.map(pledge => {
									const { value } = pledge;
									if (!(objectType || []).includes(value)) return { ...pledge, isDisabled: true };
									return pledge;
							  }),
					},
				},
				{
					isHidden: !isAutoscoring || !isAutoscoringPropery,
					label: 'Год постройки с (включительно)',
					field: {
						isDisabled: isDisabledEdit,
						isRequired: true,
						errorMessage: 'Введите год постройки',
						fieldType: 'input',
						fieldName: 'yearOfBuild',
						mask: '9999',
						placeholder: 'Год постройки/приобретения с',
						validationType: Number.isNaN(yearOfBuild) ? undefined : 'minMax',
						minValue: Number(yearOfBuild),
					},
				},*/
				{
					isHidden: !isAutoscoring,
					columns: 3,
					columnGap: 2,
					field: {
						fieldType: 'checkbox',
						label: 'Автосогласование титула',
						fieldName: 'autoscoringTitle',
						isDisabled: isDisabledEdit || !isSuper && autoscoring && !autoscoringTitle,
					},
				},
				{
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'checkbox',
						fieldName: 'declaration',
						label: 'Доступность декларации',
					},
				},
				{
					isHidden: !isDeclaration,
					field: {
						fieldType: 'checkbox',
						label: 'Декларация жизни',
						fieldName: 'declarationLife',
						isDisabled: isDisabledEdit || !isSuper && declaration && !declarationLife,
					},
				},
				{
					isHidden: !isDeclaration || !isDeclarationLife,
					label: 'Максимальная страховая сумма по заявке',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'number',
						digitsAfterDot: 2,
						isRequired: true,
						errorMessage: 'Введите максимальную страховую сумму',
						placeholder: 'Страховая сумма',
						fieldName: 'declarationSummLife',
						validationType: Number.isNaN(declarationSummLife) ? undefined : 'minMax',
						maxValue: declarationSummLife,
					},
				},
				{
					isHidden: !isDeclaration || !isDeclarationLife,
					label: 'Возрастной лимит',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'range',
						isRequired: true,
						errorMessage: 'Выберите возрастной лимит',
						placeholder: 'Возраст',
						fieldName: 'declarationAgeLimit',
						min: 0,
						max: 100,
						step: 1,
						validationType: Number.isNaN(declarationAgeLimit) ? undefined : 'minMax',
						maxValue: Number(declarationAgeLimit),
					},
				},
				{
					isHidden: !isDeclaration,
					field: {
						fieldType: 'checkbox',
						label: 'Декларация имущества',
						fieldName: 'declarationProperty',
						isDisabled: isDisabledEdit || !isSuper && declaration && !declarationProperty,
					},
				},
				{
					isHidden: !isDeclaration || !isDeclarationProperty,
					field: {
						fieldType: 'element',
						fieldName: 'declTable',
						element: propertyDeclTable
					}
				},
				/*{
					isHidden: !isDeclaration || !isDeclarationProperty,
					label: 'Максимальная страховая сумма по заявке',
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'number',
						digitsAfterDot: 2,
						isRequired: true,
						errorMessage: 'Введите максимальную страховую сумму',
						placeholder: 'Страховая сумма',
						fieldName: 'declarationSummProperty',
						validationType: Number.isNaN(declarationSummProperty) ? undefined : 'minMax',
						maxValue: declarationSummProperty,
					},
				},
				{
					isHidden: !isDeclaration || !isDeclarationProperty,
					label: 'Год постройки с (включительно)',
					field: {
						isDisabled: isDisabledEdit,
						isRequired: true,
						errorMessage: 'Введите год постройки',
						fieldType: 'input',
						fieldName: 'declarationYearOfBuild',
						mask: '9999',
						placeholder: 'Год постройки/приобретения с',
						validationType: Number.isNaN(declarationYearOfBuild) ? undefined : 'minMax',
						minValue: Number(declarationYearOfBuild),
					},
				},
				{
					isHidden: !isDeclaration || !isDeclarationProperty,
					label: 'Объекты залога',
					field: {
						isDisabled: isDisabledEdit,
						isRequired: true,
						errorMessage: 'Выберите объекты залога',
						fieldType: 'enums',
						fieldName: 'declarationObjectType',
						placeholder: 'Поиск по объектам залога',
						isLoading: loadingPledgeTypes,
						items: isSuper
							? selectPledgeTypes
							: selectPledgeTypes.map(pledge => {
									const { value } = pledge;
									if (!(declarationObjectType || []).includes(value)) return { ...pledge, isDisabled: true };
									return pledge;
							  }),
					},
				},
				{
					isHidden: !isDeclaration || !isDeclarationProperty,
					label: 'Материал стен',
					field: {
						isDisabled: isDisabledEdit,
						isRequired: true,
						errorMessage: 'Выберите материал стен',
						fieldType: 'enums',
						fieldName: 'declarationWallMaterials',
						placeholder: 'Поиск по материалам стен',
						isLoading: loadingWallMaterial,
						items: isSuper
							? selectWallMaterial
							: selectWallMaterial.map(material => {
									const { value } = material;
									if (!(declarationWallMaterials || []).includes(value)) return { ...material, isDisabled: true };
									return material;
							  }),
					},
				},
				{
					isHidden: !isDeclaration || !isDeclarationProperty,
					label: 'Материал перекрытий',
					field: {
						isDisabled: isDisabledEdit,
						isRequired: true,
						errorMessage: 'Выберите материал перекрытий',
						fieldType: 'enums',
						fieldName: 'declarationFloorMaterials',
						placeholder: 'Поиск по материалам перекрытий',
						isLoading: loadingWallMaterial,
						items: isSuper
							? selectWallMaterial
							: selectWallMaterial.map(material => {
									const { value } = material;
									if (!(declarationFloorMaterials || []).includes(value)) return { ...material, isDisabled: true };
									return material;
							  }),
					},
				},*/
				{
					isHidden: !isDeclaration,
					field: {
						fieldType: 'checkbox',
						label: 'Декларация титула',
						fieldName: 'declarationTitle',
						isDisabled: isDisabledEdit || !isSuper && declaration && !declarationTitle,
					},
				},
				{
					field: {
						isDisabled: isDisabledEdit,
						fieldType: 'textarea',
						fieldName: 'comment',
						label: 'Комментарий',
						placeholder: 'Примечание (номер заявки на портале, обоснование изменений)',
						isRequired: true,
					}
				}
			],
		}),
		[
			ageLimit,
			autoscoring,
			autoscoringLife,
			autoscoringProperty,
			autoscoringTitle,
			banks,
			contragentMaxAge,
			declaration,
			declarationAgeLimit,
			declarationLife,
			declarationProperty,
			declarationSummLife,
			declarationSummProperty,
			declarationTitle,
			declarationYearOfBuild,
			formHook,
			groupsUsers,
			isAutoscoring,
			isAutoscoringLife,
			isAutoscoringPropery,
			isDeclaration,
			isDeclarationLife,
			isDeclarationProperty,
			isSuper,
			loadingBanks,
			loadingGroups,
			loadingPledgeTypes,
			loadingUsers,
			loadingWallMaterial,
			maxDiscount,
			selectBanks,
			selectPledgeTypes,
			selectWallMaterial,
			setupDefaultValueHandler,
			summLife,
			summProperty,
			yearOfBuild,
		]
	);

	const value: SettingsContext = React.useMemo(
		() => ({
			form,
			onFormSubmit,
			isLoading: loadingUpdateRestriction,
			stateIsOpenHistory,
			setIsOpenHistory,
			onHistoryClick,
			historyData: stateHistoryData,
			isDisabledEdit,
            stateCopyConfirmModal,
            setCopyConfirmModal,
            onCopyFromConfirmed,
		}),
		[form, loadingUpdateRestriction, onFormSubmit]
	);

	return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export default React.memo(SettingsContextProvider);
